import { useCallback } from 'react';
import groupBy from 'lodash/groupBy';
import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common';
import {
	useCacheActions,
	ROOT_LEVEL,
	type BaseIssueNode,
} from '@atlassian/jira-business-list-handlers';
import { fg } from '@atlassian/jira-feature-gating';
import { LIST_VIEW_QUERY } from '../../../../services/get-list-view-issues/gql';
import { useHierarchyStore } from '../../../hierarchy';
import { useBulkSelectionStore } from '../../bulk-selection-store';
import { transformData } from './utils';

export const useBulkEditCacheActions = () => {
	const { updateItemsInCache } = useCacheActions();
	const [{ selectedIssues }] = useBulkSelectionStore();
	const [{ issuesMap }] = useHierarchyStore();

	const updateSelectedItemsInCache = useCallback(
		({ data }: IssuesUpdatePayload) => {
			const selectedIssuesByParent = groupBy(
				[...selectedIssues],
				(issueId) => issuesMap.get(issueId)?.parentIssueId || ROOT_LEVEL,
			);

			const parents = Object.entries(selectedIssuesByParent);

			const optimisticUpdateIssue = (existingIssue: BaseIssueNode) => {
				const updatedIssue = {
					...existingIssue,
					fields: [
						...(existingIssue.fields?.filter(
							(field) => !Object.keys(data).includes(field?.fieldId),
						) ?? []),
						...transformData(existingIssue, data),
					],
				};

				return updatedIssue;
			};

			parents.forEach(([, issues]) => {
				const variables = issuesMap.get(issues[0])?.cacheVariables;

				if (variables !== undefined) {
					updateItemsInCache({
						query: LIST_VIEW_QUERY,
						variables: { ...variables, withParentIssueId: fg('jsw_list_view_-_all_the_fields') },
						issueIds: issues,
						updateItem: optimisticUpdateIssue,
					});
				}
			});
		},
		[selectedIssues, issuesMap, updateItemsInCache],
	);

	return { updateSelectedItemsInCache };
};
